/**
 * Password login (ARCHITECTURE §9.1). Machine-generated passwords, entered on a fresh device, land
 * the device in PENDING (crew) or hold it for MFA (admin). No account creation here — accounts are
 * provisioned by an admin.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { login } from './api.js';
import { MfaChallenge } from './Mfa.js';

const ERRORS: Record<string, string> = {
  bad_credentials: 'Wrong username or password.',
  user_disabled: 'This account has been disabled.',
  locked: 'Too many attempts — locked for 15 minutes.',
  rate_limited: 'Too many requests — wait a moment.',
};

export function Login({ onAuthenticated }: { onAuthenticated: () => void }): ReactNode {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mfa, setMfa] = useState<{ shortId: string; code: string; expiresAt: number } | null>(null);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await login(username.trim(), password.replace(/[\s-]/g, ''));
    setBusy(false);
    if (r.kind === 'ok') {
      if (r.pairingCode) sessionStorage.setItem('lw.pairingCode', r.pairingCode);
      onAuthenticated();
    } else if (r.kind === 'mfa') setMfa({ shortId: r.shortId, code: r.code, expiresAt: r.expiresAt });
    else setError(ERRORS[r.reason] ?? 'Sign-in failed.');
  };

  if (mfa) return <MfaChallenge request={mfa} onApproved={onAuthenticated} onCancel={() => setMfa(null)} />;

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={submit}>
        <h1 className="auth-title">Sign in</h1>
        <label className="auth-field">
          <span>Username</span>
          <input autoFocus autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} data-testid="login-username" />
        </label>
        <label className="auth-field">
          <span>Password</span>
          <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" placeholder="XXXXX-XXXXX" />
        </label>
        {error ? <div className="auth-error" role="alert">{error}</div> : null}
        <button className="auth-submit" type="submit" disabled={busy || !username || !password} data-testid="login-submit">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <p className="auth-note">Your device will need approval by an admin before its first mission.</p>
      </form>
    </div>
  );
}
